/**
 * SelectedTaskItem Component
 *
 * Individual selected task item with hover-to-delete functionality.
 * Used in the wizard step to display selected related tasks.
 *
 * Features:
 * - Task title and location display
 * - Hover-to-delete (desktop: fades in, mobile: always visible)
 * - Keyboard accessible remove action
 * - 100% CSS Modules
 *
 * @module components/tasks/wizard/steps/Step4RelatedTasks/components
 */

import React, { useCallback } from 'react';
import { Group, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconTrash, IconMapPin } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { TaskListItem } from '../../../../../../types';
import { ICON_SIZE_SM, ICON_SIZE_MD } from '../../../../../../constants/ui';
import styles from './SelectedTaskItem.module.css';

interface SelectedTaskItemProps {
  task: TaskListItem;
  onRemove: (taskId: string) => void;
  variant?: 'default' | 'light';
}

export const SelectedTaskItem: React.FC<SelectedTaskItemProps> = ({
  task,
  onRemove,
  variant = 'default',
}) => {
  const { t } = useTranslation();

  const className =
    variant === 'light' ? styles.taskItemLight : styles.taskItem;

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove(task.id);
    },
    [task.id, onRemove]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onRemove(task.id);
      }
    },
    [task.id, onRemove]
  );

  return (
    <div className={className}>
      <div className={styles.taskInfo}>
        {/* Title row */}
        <Text size="sm" fw={500} className={styles.taskTitle} lineClamp={1}>
          {task.title}
        </Text>

        {/* Location row - EXACT match to TaskResultCard */}
        {task.location && (
          <Group gap="xs" className={styles.locationRow} wrap="nowrap">
            <IconMapPin size={ICON_SIZE_SM} className={styles.locationIcon} />
            <Text size="xs" c="dimmed" className={styles.locationText}>
              {task.location}
            </Text>
          </Group>
        )}
      </div>

      <Tooltip label={t('wizard.step4.removeTask')}>
        <ActionIcon
          variant="subtle"
          color="red"
          size="sm"
          onClick={handleRemove}
          onKeyDown={handleKeyDown}
          className={styles.removeButton}
          aria-label={t('wizard.step4.removeTask')}
        >
          <IconTrash size={ICON_SIZE_MD} />
        </ActionIcon>
      </Tooltip>
    </div>
  );
};
